import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import parse from 'html-react-parser';
import DisplayImage from '@/components/Ui-components/display-image';
import {colors, borderRadius, fontSize, fontWeight, letterSpacing, lineHeight, shadows, spacing} from '@/styles/tokens';

export interface SocialProofStatCard {
    value: string;
    color: string;
    label: string;
    icon: StaticImageData;
    iconH: number;
}

export interface SocialProofProps {
    sectionLabel?: string;
    clientNames: string[];
    narrativeText: string;
    statCards: SocialProofStatCard[];
}

const outerGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', lg: '7fr 5fr'},
    gap: {xs: spacing[40], lg: spacing[64]},
    alignItems: 'start'
} as const;

const leftColSx = {display: 'flex', flexDirection: 'column'} as const;

const labelSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.widest,
    color: colors.text.secondary,
    textTransform: 'uppercase' as const,
    mb: spacing[24]
} as const;

const clientPillsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[8],
    mb: spacing[40]
} as const;

const clientPillSx = {
    bgcolor: colors.background.subtle,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.full,
    px: spacing[20],
    py: spacing[8]
} as const;

const clientPillTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.semibold,
    color: colors.text.secondary,
    letterSpacing: letterSpacing.slight
} as const;

const narrativeTextSx = {
    fontSize: fontSize[20],
    lineHeight: lineHeight[28],
    mb: spacing[40]
} as const;

const statGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: spacing[24]
} as const;

const statCardSx = {
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderFaint}`,
    borderRadius: borderRadius['3xl'],
    p: spacing[24],
    boxShadow: shadows.lg,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    minHeight: '180px'
} as const;

const statValueSx = {
    fontSize: fontSize[48],
    fontWeight: fontWeight.extrabold,
    letterSpacing: letterSpacing.tight,
    lineHeight: 1
} as const;

const statLabelSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    fontWeight: fontWeight.medium,
    my: spacing[12],
    lineHeight: lineHeight[24]
} as const;

const SocialProof = ({sectionLabel = 'TRUSTED BY BUILDERS BEHIND', clientNames, narrativeText, statCards}: SocialProofProps) => {
    return (
        <MUIContainer>
            <Box sx={outerGridSx}>
                {/* Left: label + client pills + narrative */}
                <Box sx={leftColSx}>
                    <Typography className="font-regular-text" sx={labelSx}>
                        {sectionLabel}
                    </Typography>

                    <Box sx={clientPillsWrapSx}>
                        {clientNames.map((name) => (
                            <Box key={name} sx={clientPillSx}>
                                <Typography className="font-regular-text" sx={clientPillTextSx}>
                                    {name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography component="div" className="font-regular-text remove-underline" sx={narrativeTextSx}>
                        {parse(narrativeText)}
                    </Typography>
                </Box>

                {/* Right: 2×2 stat cards */}
                <Box sx={statGridSx}>
                    {statCards.map((card) => (
                        <Box key={card.value} sx={statCardSx}>
                            <Box>
                                <Typography className="font-headers" sx={{...statValueSx, color: card.color}}>
                                    {card.value}
                                </Typography>
                                <Typography className="font-regular-text" sx={statLabelSx}>
                                    {card.label}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    pt: spacing[16],
                                    width: '100%',
                                    height: card.iconH + 'px',
                                    position: 'relative'
                                }}
                            >
                                <DisplayImage src={card.icon} alt={card.label} fill />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </MUIContainer>
    );
};

export default memo(SocialProof);
